import type {
  OraclePropertyGraph,
  OracleSchemaColumn,
  OracleSchemaForeignKey,
  OracleSchemaObject,
  OracleSchemaObjectType,
  OracleSchemaPackage,
  OracleSchemaRoutine,
  OracleSchemaSequence,
  OracleSchemaSnapshot,
  OracleSchemaSynonym,
  OracleTypeAttribute,
} from './oracleSchemaTypes';
import { fetchOracleRoutineParams } from './oracleRoutineIntrospection';
import {
  mapOraclePackageSpecSourceRows,
  mapOraclePlScopeMemberRows,
  mergeOraclePackageMembers,
  parseOraclePackageSpecMembers,
} from './oraclePackageMembers';
import type { IntrospectionRow, PagedIntrospectionOutcome } from './introspectionPaging';
import {
  CHECK_ROW_BUDGET,
  COLUMN_ROW_BUDGET,
  FK_ROW_BUDGET,
  ROUTINE_ROW_BUDGET,
  SEQUENCE_ROW_BUDGET,
  TABLE_ROW_BUDGET,
  TYPE_ROW_BUDGET,
} from './introspectionBudgets';
import {
  oracleSupportsRowLimiting,
  pageOracleArtefact,
  probeRows,
  type OraclePageSink,
  type OraclePagingMode,
  type OracleRelationScoped,
  type PagedFetchOptions,
} from './oracleIntrospectionQuery';

export type { OraclePageSink, OraclePagingMode, OracleRelationScoped, PagedFetchOptions };
export { oracleSupportsRowLimiting };

const SYSTEM_SCHEMAS = new Set([
  'SYS', 'SYSTEM', 'DBSNMP', 'CTXSYS', 'XDB', 'MDSYS', 'ORDDATA', 'ORDSYS',
  'WMSYS', 'EXFSYS', 'OLAPSYS', 'OUTLN', 'DVSYS', 'LBACSYS', 'APPQOSSYS',
  'DBSFWUSER', 'GGSYS', 'GSMADMIN_INTERNAL', 'AUDSYS', 'OJVMSYS',
]);

function toNullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return String(value);
}

/** The accessible schema list — paged, since `ALL_USERS` scales with the
 *  instance rather than with the connected schema. */
export async function fetchOracleSchemaNames(
  connectionId: string,
  schema: string,
  mode?: OraclePagingMode,
): Promise<string[]> {
  const out: string[] = [];
  await pageOracleArtefact<string>({
    connectionId,
    schema,
    label: 'schemas',
    artefact: 'tables',
    totalBudget: TABLE_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT USERNAME FROM ALL_USERS ORDER BY USERNAME ${pageClause}`,
    aggregate: (rows) =>
      rows
        .map((row) => String(row.USERNAME ?? ''))
        .filter((name) => name && !SYSTEM_SCHEMAS.has(name.toUpperCase())),
    sink: (page) => {
      out.push(...page);
    },
    mode,
  });
  return out;
}

/**
 * Probe the Oracle server version once per snapshot refresh so IntelliSense
 * version gating (INT-1) can compare it against feature `addedIn` versions.
 * `product_component_version` is readable by every user and returns the
 * dotted release (e.g. `"19.0.0.0.0"`). Returns `''` on failure — the bus
 * projector maps empty to `undefined` ("unknown / don't gate").
 *
 * VERSION_FULL carries the RU (e.g. 23.26.1.0.0); VERSION is major-only
 * (23.0.0.0.0). VERSION_FULL exists 18c+; older servers error → fall back.
 * PRODUCT match is intentionally broad: 26ai reports 'Oracle AI Database
 * 26ai Free' (probe-pinned 2026-07-10), which 'Oracle Database%' misses.
 */
export async function fetchOracleServerVersion(connectionId: string, schema: string): Promise<string> {
  const full = await probeRows(connectionId, schema, 'version-full',
    `SELECT VERSION_FULL FROM product_component_version WHERE PRODUCT LIKE 'Oracle%Database%' AND ROWNUM = 1`);
  const fullValue = full[0] ? String(full[0].VERSION_FULL ?? '') : '';
  if (fullValue) return fullValue;
  const basic = await probeRows(connectionId, schema, 'version',
    `SELECT VERSION FROM product_component_version WHERE PRODUCT LIKE 'Oracle%Database%' AND ROWNUM = 1`);
  return basic[0] ? String(basic[0].VERSION ?? '') : '';
}

export type OracleRelationEntry = {
  name: string;
  owner: string;
  type: OracleSchemaObjectType;
};

export function aggregateOracleRelationRows(
  rows: readonly IntrospectionRow[],
): OracleRelationEntry[] {
  const out: OracleRelationEntry[] = [];
  for (const row of rows) {
    const name = String(row.OBJECT_NAME ?? '').trim();
    if (!name) continue;
    const rawType = String(row.OBJECT_TYPE ?? '').toUpperCase();
    out.push({
      name,
      owner: String(row.OWNER ?? '').trim(),
      type:
        rawType === 'VIEW'
          ? 'view'
          : rawType === 'MATERIALIZED VIEW'
            ? 'materialized_view'
            : 'table',
    });
  }
  return out;
}

/** Page the relation list into `sink` — PHASE 1 of the tiered load. */
export async function fetchOracleRelationPages(
  connectionId: string,
  schema: string,
  sink: OraclePageSink<OracleRelationEntry>,
  mode?: OraclePagingMode,
): Promise<void> {
  await pageOracleArtefact<OracleRelationEntry>({
    connectionId,
    schema,
    label: 'relations',
    artefact: 'tables',
    totalBudget: TABLE_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT OWNER, OBJECT_NAME, OBJECT_TYPE FROM ALL_OBJECTS
    WHERE OBJECT_TYPE IN ('TABLE', 'VIEW', 'MATERIALIZED VIEW')
    AND OWNER = '${schema.toUpperCase()}'
    ORDER BY OBJECT_NAME ${pageClause}`,
    aggregate: aggregateOracleRelationRows,
    sink,
    mode,
  });
}

/** Accumulating wrapper over {@link fetchOracleRelationPages}. */
async function fetchTablesAndViews(
  connectionId: string,
  schema: string,
): Promise<OracleRelationEntry[]> {
  const out: OracleRelationEntry[] = [];
  await fetchOracleRelationPages(connectionId, schema, (page) => {
    out.push(...page);
  });
  return out;
}

export function aggregateOracleColumnRows(
  rows: readonly IntrospectionRow[],
  primaryKeys?: ReadonlyMap<string, ReadonlySet<string>>,
): OracleRelationScoped<OracleSchemaColumn>[] {
  const out: OracleRelationScoped<OracleSchemaColumn>[] = [];
  for (const row of rows) {
    const table = String(row.TABLE_NAME ?? '');
    if (!table) continue;
    const name = String(row.COLUMN_NAME ?? '');
    out.push({
      table,
      item: {
        name,
        dataType: formatOracleDataType(
          String(row.DATA_TYPE ?? ''),
          row.DATA_PRECISION,
          row.DATA_SCALE,
          row.DATA_LENGTH,
        ),
        isNullable: String(row.NULLABLE ?? '') === 'Y',
        isPrimaryKey: primaryKeys?.get(table)?.has(name) === true,
        defaultValue: toNullableString(row.DATA_DEFAULT),
        ordinalPosition: Number(row.COLUMN_ID ?? 0),
      },
    });
  }
  return out;
}

/**
 * Page every column of the schema's tables/views into `sink` (phase 2).
 * `primaryKeys` must already be fetched — the PK flag lives in
 * `ALL_CONSTRAINTS`, and re-reading it per page would multiply round-trips.
 */
export async function fetchOracleColumnPages(
  connectionId: string,
  schema: string,
  primaryKeys: ReadonlyMap<string, ReadonlySet<string>>,
  sink: OraclePageSink<OracleRelationScoped<OracleSchemaColumn>>,
  mode?: OraclePagingMode,
): Promise<void> {
  await pageOracleArtefact<OracleRelationScoped<OracleSchemaColumn>>({
    connectionId,
    schema,
    label: 'cols',
    artefact: 'columns',
    totalBudget: COLUMN_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT atc.TABLE_NAME, atc.COLUMN_NAME, atc.DATA_TYPE,
    atc.DATA_LENGTH, atc.DATA_PRECISION, atc.DATA_SCALE, atc.NULLABLE,
    atc.DATA_DEFAULT, atc.COLUMN_ID
    FROM ALL_TAB_COLUMNS atc
    WHERE atc.OWNER = '${schema.toUpperCase()}'
    ORDER BY atc.TABLE_NAME, atc.COLUMN_ID ${pageClause}`,
    aggregate: (rows) => aggregateOracleColumnRows(rows, primaryKeys),
    sink,
    mode,
  });
}

/** Accumulating wrapper over {@link fetchOracleColumnPages}. */
async function fetchColumns(
  connectionId: string,
  schema: string,
  primaryKeys: ReadonlyMap<string, ReadonlySet<string>> = new Map(),
): Promise<Record<string, OracleSchemaColumn[]>> {
  const byTable: Record<string, OracleSchemaColumn[]> = {};
  await fetchOracleColumnPages(connectionId, schema, primaryKeys, (page) => {
    for (const { table, item } of page) (byTable[table] ??= []).push(item);
  });
  return byTable;
}

function formatOracleDataType(
  dataType: string,
  precision: unknown,
  scale: unknown,
  length: unknown,
): string {
  const dt = dataType.toUpperCase();
  if (dt === 'NUMBER') {
    if (precision != null && Number(precision) > 0) {
      return scale != null && Number(scale) > 0
        ? `NUMBER(${precision},${scale})`
        : `NUMBER(${precision})`;
    }
    return 'NUMBER';
  }
  if (dt === 'VARCHAR2' || dt === 'NVARCHAR2' || dt === 'CHAR' || dt === 'NCHAR' || dt === 'RAW') {
    return length != null ? `${dt}(${length})` : dt;
  }
  return dt;
}

/**
 * Fetch raw `SEARCH_CONDITION` strings for `CONSTRAINT_TYPE = 'C'` (CHECK)
 * constraints, grouped by table name. The JSON-classification cascade
 * (`detectOracleIsJsonColumns` in `sql-resolver/json/classify/`) consumes
 * these strings to surface columns governed by `IS JSON` / `IS JSON STRICT`
 * as `check-isjson`. Filtered to user-defined constraints (`GENERATED = 'USER NAME'`)
 * so the per-engine detector does not have to ignore Oracle's auto-generated
 * NOT NULL constraints.
 */
/** Paged by `(TABLE_NAME, CONSTRAINT_NAME)` — the ORDER BY the page window
 *  needs, which the pre-S7 un-paged query did not have to declare. */
export async function fetchOracleCheckConstraintPages(
  connectionId: string,
  schema: string,
  sink: OraclePageSink<OracleRelationScoped<string>>,
  mode?: OraclePagingMode,
): Promise<PagedIntrospectionOutcome> {
  return pageOracleArtefact<OracleRelationScoped<string>>({
    connectionId,
    schema,
    label: 'checks',
    artefact: 'checks',
    totalBudget: CHECK_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT TABLE_NAME, CONSTRAINT_NAME, SEARCH_CONDITION
    FROM ALL_CONSTRAINTS
    WHERE CONSTRAINT_TYPE = 'C'
      AND OWNER = '${schema.toUpperCase()}'
      AND GENERATED = 'USER NAME'
      AND SEARCH_CONDITION IS NOT NULL
    ORDER BY TABLE_NAME, CONSTRAINT_NAME ${pageClause}`,
    aggregate: (rows) => {
      const out: OracleRelationScoped<string>[] = [];
      for (const row of rows) {
        const table = String(row.TABLE_NAME ?? '');
        const clause = toNullableString(row.SEARCH_CONDITION);
        if (!table || !clause) continue;
        out.push({ table, item: clause });
      }
      return out;
    },
    sink,
    mode,
  });
}

/** Accumulating wrapper over {@link fetchOracleCheckConstraintPages}. */
async function fetchCheckConstraints(
  connectionId: string,
  schema: string,
): Promise<Record<string, string[]>> {
  const byTable: Record<string, string[]> = {};
  await fetchOracleCheckConstraintPages(connectionId, schema, (page) => {
    for (const { table, item } of page) (byTable[table] ??= []).push(item);
  });
  return byTable;
}

/**
 * The PK columns of every table, as a `table -> Set<column>` map. Paged by
 * `(TABLE_NAME, COLUMN_NAME)`; reported under the `columns` artefact because
 * that is the completion vocabulary a clip would degrade (the PK flag rides on
 * a column, it is not an artefact of its own).
 */
export async function fetchOraclePrimaryKeys(
  connectionId: string,
  schema: string,
  mode?: OraclePagingMode,
): Promise<Map<string, Set<string>>> {
  const byTable = new Map<string, Set<string>>();
  await pageOracleArtefact<{ table: string; column: string }>({
    connectionId,
    schema,
    label: 'pks',
    artefact: 'columns',
    totalBudget: COLUMN_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT acc.TABLE_NAME, acc.COLUMN_NAME
    FROM ALL_CONSTRAINTS ac
    JOIN ALL_CONS_COLUMNS acc ON ac.CONSTRAINT_NAME = acc.CONSTRAINT_NAME AND ac.OWNER = acc.OWNER
    WHERE ac.CONSTRAINT_TYPE = 'P' AND ac.OWNER = '${schema.toUpperCase()}'
    ORDER BY acc.TABLE_NAME, acc.COLUMN_NAME ${pageClause}`,
    aggregate: (rows) =>
      rows.map((row) => ({
        table: String(row.TABLE_NAME ?? ''),
        column: String(row.COLUMN_NAME ?? ''),
      })),
    sink: (page) => {
      for (const { table, column } of page) {
        const set = byTable.get(table);
        if (set) set.add(column);
        else byTable.set(table, new Set([column]));
      }
    },
    mode,
  });
  return byTable;
}

export async function fetchOracleSequencePages(
  connectionId: string,
  schema: string,
  sink: OraclePageSink<OracleSchemaSequence>,
  mode?: OraclePagingMode,
): Promise<void> {
  await pageOracleArtefact<OracleSchemaSequence>({
    connectionId,
    schema,
    label: 'sequences',
    artefact: 'sequences',
    totalBudget: SEQUENCE_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT SEQUENCE_OWNER, SEQUENCE_NAME FROM ALL_SEQUENCES
    WHERE SEQUENCE_OWNER = '${schema.toUpperCase()}' ORDER BY SEQUENCE_NAME ${pageClause}`,
    aggregate: (rows) =>
      rows.map((row) => ({
        name: String(row.SEQUENCE_NAME ?? ''),
        schema: String(row.SEQUENCE_OWNER ?? ''),
      })),
    sink,
    mode,
  });
}

/** Accumulating wrapper over {@link fetchOracleSequencePages}. */
async function fetchSequences(connectionId: string, schema: string): Promise<OracleSchemaSequence[]> {
  const out: OracleSchemaSequence[] = [];
  await fetchOracleSequencePages(connectionId, schema, (page) => {
    out.push(...page);
  });
  return out;
}

/**
 * Fetch user-defined type names (`ALL_TYPES` — OBJECT / VARRAY / nested-table /
 * collection types) owned by the schema. Surfaced at data-type slots
 * (`CREATE TABLE t (addr ▮)`) alongside the built-in types. Returns the bare
 * `TYPE_NAME` (verbatim Oracle uppercase). Empty on failure / no user types.
 */
export async function fetchOracleUserTypePages(
  connectionId: string,
  schema: string,
  sink: OraclePageSink<string>,
  mode?: OraclePagingMode,
): Promise<void> {
  await pageOracleArtefact<string>({
    connectionId,
    schema,
    label: 'types',
    artefact: 'types',
    totalBudget: TYPE_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT TYPE_NAME FROM ALL_TYPES
    WHERE OWNER = '${schema.toUpperCase()}' ORDER BY TYPE_NAME ${pageClause}`,
    aggregate: (rows) =>
      rows.map((row) => String(row.TYPE_NAME ?? '').trim()).filter((name) => name.length > 0),
    sink,
    mode,
  });
}

/** Accumulating wrapper over {@link fetchOracleUserTypePages}. */
async function fetchUserTypes(connectionId: string, schema: string): Promise<string[]> {
  const out: string[] = [];
  await fetchOracleUserTypePages(connectionId, schema, (page) => {
    out.push(...page);
  });
  return out;
}

/**
 * OBJECT-type attribute members (`ALL_TYPE_ATTRS`) owned by the snapshot
 * schema, grouped by uppercase type name and ordered by attribute position.
 * `ADDRESS_TYPE` → `[{ name: 'STREET', dataType: 'VARCHAR2', ordinal: 1 }, …]`.
 * Empty on failure / no OBJECT types. Drives `t.objcol.▮` attribute access.
 */
/** OBJECT-type attributes, paged by `(TYPE_NAME, ATTR_NO)`. One type is many
 *  rows, so a straddling type is carried to the next page. */
export async function fetchOracleTypeAttributePages(
  connectionId: string,
  schema: string,
  sink: OraclePageSink<{ typeName: string; attribute: OracleTypeAttribute }>,
  mode?: OraclePagingMode,
): Promise<void> {
  await pageOracleArtefact<{ typeName: string; attribute: OracleTypeAttribute }>({
    connectionId,
    schema,
    label: 'type-attrs',
    artefact: 'types',
    totalBudget: TYPE_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT TYPE_NAME, ATTR_NAME, ATTR_TYPE_NAME, ATTR_NO
    FROM ALL_TYPE_ATTRS
    WHERE OWNER = '${schema.toUpperCase()}'
    ORDER BY TYPE_NAME, ATTR_NO ${pageClause}`,
    aggregate: (rows) => {
      const out: { typeName: string; attribute: OracleTypeAttribute }[] = [];
      for (const row of rows) {
        const typeName = String(row.TYPE_NAME ?? '').trim().toUpperCase();
        const attrName = String(row.ATTR_NAME ?? '').trim();
        if (!typeName || !attrName) continue;
        const dataType = String(row.ATTR_TYPE_NAME ?? '').trim();
        out.push({
          typeName,
          attribute: {
            name: attrName,
            dataType: dataType || 'OBJECT',
            ordinal: Number(row.ATTR_NO ?? 0),
          },
        });
      }
      return out;
    },
    sink,
    groupKeyOf: (row) => String(row.TYPE_NAME ?? ''),
    mode,
  });
}

/** Accumulating wrapper over {@link fetchOracleTypeAttributePages}. */
async function fetchUserTypeAttributes(
  connectionId: string,
  schema: string,
): Promise<Record<string, OracleTypeAttribute[]>> {
  const byType: Record<string, OracleTypeAttribute[]> = {};
  await fetchOracleTypeAttributePages(connectionId, schema, (page) => {
    for (const { typeName, attribute } of page) (byType[typeName] ??= []).push(attribute);
  });
  return byType;
}

/**
 * Fetch SQL/PGQ property graphs owned by `schema` from the Oracle 23ai+ data
 * dictionary and assemble each graph's labels (vertex/edge) + per-label
 * properties. The joins are kept app-side (four flat SELECTs) for simplicity.
 *
 * Owner-column names PROBE-PINNED against FREEPDB1 (Oracle 26ai,
 * `EMPLOYEES_GRAPH`), 2026-07-11 — every ALL_* view filters on `OWNER`
 * (NOT `GRAPH_OWNER`, which raises ORA-00904). Observed column lists:
 *   ALL_PROPERTY_GRAPHS     (OWNER, GRAPH_NAME, GRAPH_MODE, ALLOWS_MIXED_TYPES, INMEMORY)
 *   ALL_PG_ELEMENTS         (OWNER, GRAPH_NAME, ELEMENT_NAME, ELEMENT_KIND, OBJECT_OWNER, OBJECT_NAME)
 *   ALL_PG_ELEMENT_LABELS   (OWNER, GRAPH_NAME, ELEMENT_NAME, LABEL_NAME)
 *   ALL_PG_LABEL_PROPERTIES (OWNER, GRAPH_NAME, LABEL_NAME, PROPERTY_NAME, DATA_TYPE,
 *                            DATA_LENGTH, DATA_PRECISION, DATA_SCALE, …, PROPERTY_ORDER)
 *
 * Pre-23 servers lack these views: `ALL_PROPERTY_GRAPHS` raises ORA-00942, the
 * first fetch returns `success:false`, and we bail with `[]` (mirroring the
 * not-success handling of the sibling object fetches).
 */
export async function fetchOraclePropertyGraphs(
  connectionId: string,
  schema: string,
): Promise<OraclePropertyGraph[]> {
  const owner = schema.toUpperCase();
  const graphRows = await probeRows(
    connectionId,
    schema,
    'graphs',
    `SELECT GRAPH_NAME FROM ALL_PROPERTY_GRAPHS WHERE OWNER = '${owner}' ORDER BY GRAPH_NAME`,
  );
  if (graphRows.length === 0) return []; // pre-23 servers: ORA-00942 → empty
  const elementRows = await probeRows(
    connectionId,
    schema,
    'graph-elements',
    `SELECT GRAPH_NAME, ELEMENT_NAME, ELEMENT_KIND FROM ALL_PG_ELEMENTS WHERE OWNER = '${owner}'`,
  );
  const labelRows = await probeRows(
    connectionId,
    schema,
    'graph-labels',
    `SELECT GRAPH_NAME, ELEMENT_NAME, LABEL_NAME FROM ALL_PG_ELEMENT_LABELS
     WHERE OWNER = '${owner}' ORDER BY GRAPH_NAME, LABEL_NAME`,
  );
  const propRows = await probeRows(
    connectionId,
    schema,
    'graph-props',
    `SELECT GRAPH_NAME, LABEL_NAME, PROPERTY_NAME, DATA_TYPE FROM ALL_PG_LABEL_PROPERTIES
     WHERE OWNER = '${owner}' ORDER BY GRAPH_NAME, LABEL_NAME, PROPERTY_ORDER`,
  );

  // ELEMENT_KIND of the element carrying a label decides vertex vs edge.
  const kindByGraphElement = new Map<string, 'vertex' | 'edge'>();
  for (const e of elementRows) {
    const kind = String(e.ELEMENT_KIND ?? '').toUpperCase() === 'EDGE' ? 'edge' : 'vertex';
    kindByGraphElement.set(`${e.GRAPH_NAME}|${e.ELEMENT_NAME}`, kind);
  }

  // Labels of a graph = union across its elements, kept in first-seen order.
  const kindByGraphLabel = new Map<string, 'vertex' | 'edge'>();
  const labelNamesByGraph = new Map<string, string[]>();
  for (const l of labelRows) {
    const graph = String(l.GRAPH_NAME ?? '');
    const label = String(l.LABEL_NAME ?? '');
    if (!graph || !label) continue;
    kindByGraphLabel.set(
      `${graph}|${label}`,
      kindByGraphElement.get(`${graph}|${l.ELEMENT_NAME}`) ?? 'vertex',
    );
    const list = labelNamesByGraph.get(graph) ?? [];
    if (!list.includes(label)) list.push(label);
    labelNamesByGraph.set(graph, list);
  }

  const propsByGraphLabel = new Map<string, { name: string; dataType?: string }[]>();
  for (const p of propRows) {
    const key = `${p.GRAPH_NAME}|${p.LABEL_NAME}`;
    const list = propsByGraphLabel.get(key) ?? [];
    list.push({
      name: String(p.PROPERTY_NAME),
      ...(p.DATA_TYPE ? { dataType: String(p.DATA_TYPE) } : {}),
    });
    propsByGraphLabel.set(key, list);
  }

  return graphRows.map((g) => {
    const name = String(g.GRAPH_NAME);
    const labelNames = labelNamesByGraph.get(name) ?? [];
    return {
      name,
      owner,
      labels: labelNames.map((ln) => ({
        name: ln,
        kind: kindByGraphLabel.get(`${name}|${ln}`) ?? 'vertex',
        properties: propsByGraphLabel.get(`${name}|${ln}`) ?? [],
      })),
    };
  });
}

export async function fetchOracleSynonyms(
  connectionId: string,
  schema: string,
  mode?: OraclePagingMode,
): Promise<OracleSchemaSynonym[]> {
  // Three-step resolution: local → private synonym (OWNER = user) → public
  // synonym (OWNER = 'PUBLIC'). Both rows ship in one trip; the EXISTS clause
  // limits PUBLIC to synonyms whose target the user can actually see, so a
  // typical schema gets ~tens of public rows rather than the thousand-odd
  // bare-PUBLIC list. The OWNER column lets the resolver mark `isPublic`
  // without a second query.
  const upper = schema.toUpperCase();
  const out: OracleSchemaSynonym[] = [];
  // Reported under `tables`: a synonym is an alias for a relation, so a clip
  // degrades exactly the relation-name vocabulary that bucket describes.
  await pageOracleArtefact<OracleSchemaSynonym>({
    connectionId,
    schema,
    label: 'synonyms',
    artefact: 'tables',
    totalBudget: TABLE_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT SYNONYM_NAME, TABLE_OWNER, TABLE_NAME, OWNER FROM ALL_SYNONYMS s
    WHERE (OWNER = '${upper}' OR OWNER = 'PUBLIC')
      AND EXISTS (SELECT 1 FROM ALL_OBJECTS o
                  WHERE o.OWNER = s.TABLE_OWNER
                    AND o.OBJECT_NAME = s.TABLE_NAME
                    AND o.OBJECT_TYPE IN ('TABLE','VIEW','MATERIALIZED VIEW','PACKAGE','PROCEDURE','FUNCTION','SEQUENCE','TYPE'))
    ORDER BY SYNONYM_NAME ${pageClause}`,
    aggregate: (rows) =>
      rows.map((row) => ({
        name: String(row.SYNONYM_NAME ?? ''),
        targetSchema: String(row.TABLE_OWNER ?? ''),
        targetName: String(row.TABLE_NAME ?? ''),
        isPublic: String(row.OWNER ?? '').toUpperCase() === 'PUBLIC',
      })),
    sink: (page) => {
      out.push(...page);
    },
    mode,
  });
  return out;
}

export async function fetchOracleRoutinePages(
  connectionId: string,
  schema: string,
  sink: OraclePageSink<OracleSchemaRoutine>,
  mode?: OraclePagingMode,
): Promise<void> {
  await pageOracleArtefact<OracleSchemaRoutine>({
    connectionId,
    schema,
    label: 'routines',
    artefact: 'routines',
    totalBudget: ROUTINE_ROW_BUDGET,
    sqlForPage: (pageClause) => `SELECT OBJECT_NAME, OBJECT_TYPE FROM ALL_PROCEDURES
    WHERE OWNER = '${schema.toUpperCase()}'
    AND OBJECT_TYPE IN ('PROCEDURE', 'FUNCTION')
    AND PROCEDURE_NAME IS NULL
    ORDER BY OBJECT_NAME ${pageClause}`,
    aggregate: (rows) =>
      rows.map((row) => ({
        name: String(row.OBJECT_NAME ?? ''),
        type: (String(row.OBJECT_TYPE ?? '').toUpperCase() === 'FUNCTION'
          ? 'function'
          : 'procedure') as 'function' | 'procedure',
        schema: schema.toUpperCase(),
      })),
    sink,
    mode,
  });
}

/** Accumulating wrapper over {@link fetchOracleRoutinePages}. */
async function fetchRoutines(connectionId: string, schema: string): Promise<OracleSchemaRoutine[]> {
  const out: OracleSchemaRoutine[] = [];
  await fetchOracleRoutinePages(connectionId, schema, (page) => {
    out.push(...page);
  });
  return out;
}

export async function fetchOraclePackages(
  connectionId: string,
  schema: string,
  mode?: OraclePagingMode,
): Promise<OracleSchemaPackage[]> {
  // Fetch packages owned by the user's schema AND accessible via public synonyms
  // (e.g. DBMS_OUTPUT, DBMS_LOB, UTL_FILE are SYS-owned but accessible via public synonyms).
  // OWNER rides along in both branches of the UNION (the user's schema for
  // the first, the literal TABLE_OWNER — always 'SYS' per the WHERE clause —
  // for the second) so the resolver can tell a bounded handful of built-in
  // SYS packages apart from the schema's own (unbounded) packages instead of
  // inferring it from an ALL_PROCEDURES membership gap (register #F4).
  const pkgSql = `SELECT OBJECT_NAME, OWNER FROM ALL_OBJECTS
    WHERE OWNER = '${schema.toUpperCase()}' AND OBJECT_TYPE = 'PACKAGE'
    UNION
    SELECT SYNONYM_NAME AS OBJECT_NAME, TABLE_OWNER AS OWNER FROM ALL_SYNONYMS
    WHERE OWNER = 'PUBLIC' AND TABLE_OWNER = 'SYS'
    AND EXISTS (SELECT 1 FROM ALL_OBJECTS o WHERE o.OWNER = TABLE_OWNER AND o.OBJECT_NAME = TABLE_NAME AND o.OBJECT_TYPE = 'PACKAGE')
    ORDER BY OBJECT_NAME`;
  const packageRows: { name: string; owner: string }[] = [];
  await pageOracleArtefact<{ name: string; owner: string }>({
    connectionId,
    schema,
    label: 'packages',
    artefact: 'routines',
    totalBudget: ROUTINE_ROW_BUDGET,
    sqlForPage: (pageClause) => `${pkgSql} ${pageClause}`,
    aggregate: (rows) =>
      rows.map((row) => ({
        name: String(row.OBJECT_NAME ?? ''),
        owner: String(row.OWNER ?? ''),
      })),
    sink: (page) => {
      packageRows.push(...page);
    },
    mode,
  });
  if (packageRows.length === 0) return [];

  // Dedup by name, preferring the connected-schema-owned row over a SYS row
  // reached via public synonym. Oracle itself resolves the LOCAL package
  // first when a schema-owned package shares a name with a SYS built-in
  // (e.g. a user-defined DBMS_OUTPUT) — but the UNION above still returns
  // both rows. Without this, `packageNames` carries duplicates, the
  // name-only system set wrongly flags both as system, and (below) SYS
  // members bleed into the local package's member list (codex P2 #1).
  const upperSchema = schema.toUpperCase();
  const winningRowByName = new Map<string, { name: string; owner: string }>();
  for (const row of packageRows) {
    const existing = winningRowByName.get(row.name);
    if (!existing || (existing.owner.toUpperCase() !== upperSchema && row.owner.toUpperCase() === upperSchema)) {
      winningRowByName.set(row.name, row);
    }
  }
  const winningRows = [...winningRowByName.values()];
  const packageNames = winningRows.map((row) => row.name);
  const systemPackageNames = new Set(
    winningRows.filter((row) => row.owner.toUpperCase() === 'SYS').map((row) => row.name),
  );

  // Fetch members for user-owned packages. Three per-schema bulk queries, run
  // together so the round-trip count per refresh stays flat in the number of
  // packages:
  //   - ALL_PROCEDURES  — the SUBPROGRAM names (no kind: `OBJECT_TYPE` for a
  //                       packaged member is always 'PACKAGE', and it never
  //                       lists constants / variables / types / cursors /
  //                       exceptions).
  //   - ALL_SOURCE      — the package SPEC text, parsed for names AND kinds.
  //                       This is the path that must work: PL/Scope is off by
  //                       default (`PLSCOPE_SETTINGS=IDENTIFIERS:NONE`).
  //   - ALL_IDENTIFIERS — PL/Scope declarations, which carry the kind
  //                       directly when the unit was compiled with
  //                       `IDENTIFIERS:ALL`. Empty (or ORA-00942 on old
  //                       servers) otherwise, hence best-effort.
  const memberSql = `SELECT OBJECT_NAME, PROCEDURE_NAME FROM ALL_PROCEDURES
    WHERE OWNER = '${upperSchema}'
    AND OBJECT_TYPE = 'PACKAGE'
    AND PROCEDURE_NAME IS NOT NULL
    ORDER BY OBJECT_NAME, PROCEDURE_NAME`;
  const specSourceSql = `SELECT NAME, LINE, TEXT FROM ALL_SOURCE
    WHERE OWNER = '${upperSchema}'
    AND TYPE = 'PACKAGE'
    ORDER BY NAME, LINE`;
  const plScopeSql = `SELECT OBJECT_NAME, NAME, TYPE FROM ALL_IDENTIFIERS
    WHERE OWNER = '${upperSchema}'
    AND OBJECT_TYPE = 'PACKAGE'
    AND USAGE = 'DECLARATION'
    AND USAGE_CONTEXT_ID = 1
    ORDER BY OBJECT_NAME, USAGE_ID`;
  // Each of the three is paged in its own right — `ALL_SOURCE` in particular
  // is one row per LINE of every package spec, easily the biggest artefact an
  // Oracle schema has. `groupKeyOf` keeps a package's rows whole across a page
  // boundary so the spec-text parse never sees half a declaration.
  const membersByPkg: Record<string, string[]> = {};
  const specSourceRows: IntrospectionRow[] = [];
  const plScopeRows: IntrospectionRow[] = [];
  await Promise.all([
    pageOracleArtefact<{ pkg: string; member: string }>({
      connectionId,
      schema,
      label: 'pkg-members',
      artefact: 'routines',
      totalBudget: ROUTINE_ROW_BUDGET,
      sqlForPage: (pageClause) => `${memberSql} ${pageClause}`,
      aggregate: (rows) =>
        rows.map((row) => ({
          pkg: String(row.OBJECT_NAME ?? ''),
          member: String(row.PROCEDURE_NAME ?? ''),
        })),
      sink: (page) => {
        for (const { pkg, member } of page) (membersByPkg[pkg] ??= []).push(member);
      },
      groupKeyOf: (row) => String(row.OBJECT_NAME ?? ''),
      mode,
    }),
    // Spec text and PL/Scope are ENRICHMENT of the member list (kinds and
    // declaration order); both are documented best-effort above, so their
    // failure degrades to `kind: 'unknown'` rather than failing the packages
    // step — the pager rejects on a failed page since release review F3.
    pageOracleArtefact<IntrospectionRow>({
      connectionId,
      schema,
      label: 'pkg-source',
      artefact: 'routines',
      totalBudget: ROUTINE_ROW_BUDGET,
      sqlForPage: (pageClause) => `${specSourceSql} ${pageClause}`,
      aggregate: (rows) => rows.slice(),
      sink: (page) => {
        specSourceRows.push(...page);
      },
      groupKeyOf: (row) => String(row.NAME ?? ''),
      mode,
    }).catch(() => undefined),
    pageOracleArtefact<IntrospectionRow>({
      connectionId,
      schema,
      label: 'pkg-plscope',
      artefact: 'routines',
      totalBudget: ROUTINE_ROW_BUDGET,
      sqlForPage: (pageClause) => `${plScopeSql} ${pageClause}`,
      aggregate: (rows) => rows.slice(),
      sink: (page) => {
        plScopeRows.push(...page);
      },
      groupKeyOf: (row) => String(row.OBJECT_NAME ?? ''),
      mode,
    }).catch(() => undefined),
  ]);

  const specSourceByPkg = mapOraclePackageSpecSourceRows(specSourceRows);
  const plScopeMembersByPkg = mapOraclePlScopeMemberRows(plScopeRows);

  // Fetch members for SYS packages accessible via public synonyms
  const sysPackageNames = packageNames.filter((name) => systemPackageNames.has(name));
  if (sysPackageNames.length > 0) {
    const inList = sysPackageNames.map((n) => `'${n}'`).join(',');
    const sysMemberSql = `SELECT OBJECT_NAME, PROCEDURE_NAME FROM ALL_PROCEDURES
      WHERE OWNER = 'SYS'
      AND OBJECT_TYPE = 'PACKAGE'
      AND OBJECT_NAME IN (${inList})
      AND PROCEDURE_NAME IS NOT NULL
      ORDER BY OBJECT_NAME, PROCEDURE_NAME`;
    await pageOracleArtefact<{ pkg: string; member: string }>({
      connectionId,
      schema,
      label: 'sys-pkg-members',
      artefact: 'routines',
      totalBudget: ROUTINE_ROW_BUDGET,
      sqlForPage: (pageClause) => `${sysMemberSql} ${pageClause}`,
      aggregate: (rows) =>
        rows.map((row) => ({
          pkg: String(row.OBJECT_NAME ?? ''),
          member: String(row.PROCEDURE_NAME ?? ''),
        })),
      sink: (page) => {
        for (const { pkg, member } of page) (membersByPkg[pkg] ??= []).push(member);
      },
      groupKeyOf: (row) => String(row.OBJECT_NAME ?? ''),
      mode,
    });
  }

  // Kind resolution per package: PL/Scope first (it carries the kind
  // verbatim), then the spec-text parse, then nothing — in which case the
  // ALL_PROCEDURES names still surface, with kind `'unknown'`.
  return packageNames.map((name) => {
    const typedMembers = plScopeMembersByPkg[name]?.length
      ? plScopeMembersByPkg[name]!
      : parseOraclePackageSpecMembers(specSourceByPkg[name] ?? '');
    return {
      name,
      schema: upperSchema,
      members: mergeOraclePackageMembers(membersByPkg[name] ?? [], typedMembers),
      ...(systemPackageNames.has(name) ? { isSystemPackage: true } : {}),
    };
  });
}

/**
 * Fetch FK edges from `ALL_CONSTRAINTS` × `ALL_CONS_COLUMNS`. Joins back to
 * the parent constraint (`R_CONSTRAINT_NAME`/`R_OWNER`) to recover the
 * referenced (parent) side's columns. Composite keys arrive as multiple
 * rows ordered by `POSITION`; the renderer-side `aggregateOracleFkRows`
 * groups them by `(OWNER, CONSTRAINT_NAME)`.
 *
 * Filtered to the connected user's schema for symmetry with the rest of the
 * Oracle introspection (every other fetcher uses `OWNER = '<schema>'`); the
 * referenced side may live in another schema, so `ToOwner` is preserved.
 */
export function buildOracleForeignKeysPageSql(schema: string, pageClause: string): string {
  return `SELECT
  c.OWNER         AS FROM_OWNER,
  c.TABLE_NAME    AS FROM_TABLE,
  c.CONSTRAINT_NAME AS FK_NAME,
  cc_from.COLUMN_NAME AS FROM_COLUMN,
  cc_from.POSITION    AS ORD,
  rc.OWNER        AS TO_OWNER,
  rc.TABLE_NAME   AS TO_TABLE,
  cc_to.COLUMN_NAME AS TO_COLUMN
FROM ALL_CONSTRAINTS c
JOIN ALL_CONS_COLUMNS cc_from
  ON cc_from.OWNER = c.OWNER
 AND cc_from.CONSTRAINT_NAME = c.CONSTRAINT_NAME
 AND cc_from.TABLE_NAME = c.TABLE_NAME
JOIN ALL_CONSTRAINTS rc
  ON rc.OWNER = c.R_OWNER
 AND rc.CONSTRAINT_NAME = c.R_CONSTRAINT_NAME
JOIN ALL_CONS_COLUMNS cc_to
  ON cc_to.OWNER = rc.OWNER
 AND cc_to.CONSTRAINT_NAME = rc.CONSTRAINT_NAME
 AND cc_to.TABLE_NAME = rc.TABLE_NAME
 AND cc_to.POSITION = cc_from.POSITION
WHERE c.CONSTRAINT_TYPE = 'R'
  AND c.OWNER = '${schema.toUpperCase()}'
ORDER BY c.OWNER, c.CONSTRAINT_NAME, cc_from.POSITION ${pageClause}`;
}

/**
 * Page the FK graph into `sink`. A composite FK is one row per key column, so
 * a straddling `(OWNER, CONSTRAINT_NAME)` group is carried to the next page —
 * otherwise one constraint would aggregate into two half-edges.
 */
export async function fetchOracleForeignKeyPages(
  connectionId: string,
  schema: string,
  sink: OraclePageSink<OracleSchemaForeignKey>,
  mode?: OraclePagingMode,
): Promise<void> {
  await pageOracleArtefact<OracleSchemaForeignKey>({
    connectionId,
    schema,
    label: 'fks',
    artefact: 'foreignKeys',
    totalBudget: FK_ROW_BUDGET,
    sqlForPage: (pageClause) => buildOracleForeignKeysPageSql(schema, pageClause),
    aggregate: aggregateOracleFkRows,
    sink,
    groupKeyOf: (row) => `${String(row.FROM_OWNER ?? '')}|${String(row.FK_NAME ?? '')}`,
    mode,
  });
}

/** Accumulating wrapper over {@link fetchOracleForeignKeyPages}. */
async function fetchForeignKeys(
  connectionId: string,
  schema: string,
): Promise<OracleSchemaForeignKey[]> {
  const out: OracleSchemaForeignKey[] = [];
  await fetchOracleForeignKeyPages(connectionId, schema, (page) => {
    out.push(...page);
  });
  return out;
}

/**
 * Aggregate per-column FK rows from `ALL_CONS_COLUMNS` into one
 * `OracleSchemaForeignKey` per `(OWNER, CONSTRAINT_NAME)`. Exported so unit
 * tests can exercise the grouping without an IPC bridge.
 */
export function aggregateOracleFkRows(
  rows: readonly Record<string, unknown>[],
): OracleSchemaForeignKey[] {
  type Pending = {
    fromOwner: string;
    fromTable: string;
    name: string;
    toOwner: string;
    toTable: string;
    fromCols: { ord: number; col: string }[];
    toCols: { ord: number; col: string }[];
  };

  const byKey = new Map<string, Pending>();
  for (const raw of rows) {
    const fromOwner = String(raw.FROM_OWNER ?? '').trim();
    const fromTable = String(raw.FROM_TABLE ?? '').trim();
    const fkName = String(raw.FK_NAME ?? '').trim();
    const fromColumn = String(raw.FROM_COLUMN ?? '').trim();
    const toOwner = String(raw.TO_OWNER ?? '').trim();
    const toTable = String(raw.TO_TABLE ?? '').trim();
    const toColumn = String(raw.TO_COLUMN ?? '').trim();
    if (!fromTable || !toTable || !fromColumn || !toColumn || !fkName) continue;
    const ord = Number(raw.ORD ?? 0);
    const key = `${fromOwner}|${fkName}`;
    let entry = byKey.get(key);
    if (!entry) {
      entry = {
        fromOwner,
        fromTable,
        name: fkName,
        toOwner,
        toTable,
        fromCols: [],
        toCols: [],
      };
      byKey.set(key, entry);
    }
    entry.fromCols.push({ ord, col: fromColumn });
    entry.toCols.push({ ord, col: toColumn });
  }

  const out: OracleSchemaForeignKey[] = [];
  for (const entry of byKey.values()) {
    entry.fromCols.sort((a, b) => a.ord - b.ord);
    entry.toCols.sort((a, b) => a.ord - b.ord);
    out.push({
      name: entry.name,
      fromSchema: entry.fromOwner || undefined,
      fromTable: entry.fromTable,
      fromColumns: entry.fromCols.map((c) => c.col),
      toSchema: entry.toOwner || undefined,
      toTable: entry.toTable,
      toColumns: entry.toCols.map((c) => c.col),
    });
  }
  return out;
}

function buildLookupIndexes(objects: OracleSchemaObject[]): Pick<
  OracleSchemaSnapshot,
  'objectsByNameLower' | 'columnsByObjectNameLower' | 'columnIndexByNameLower'
> {
  const objectsByNameLower: OracleSchemaSnapshot['objectsByNameLower'] = {};
  const columnsByObjectNameLower: OracleSchemaSnapshot['columnsByObjectNameLower'] = {};
  const columnIndexByNameLower: OracleSchemaSnapshot['columnIndexByNameLower'] = {};

  for (const obj of objects) {
    const key = obj.name.toLowerCase();
    objectsByNameLower[key] = obj;
    columnsByObjectNameLower[key] = obj.columns;

    for (const column of obj.columns) {
      const columnKey = column.name.toLowerCase();
      const list = columnIndexByNameLower[columnKey] ?? [];
      list.push({ objectName: obj.name, objectType: obj.type, column });
      columnIndexByNameLower[columnKey] = list;
    }
  }

  return { objectsByNameLower, columnsByObjectNameLower, columnIndexByNameLower };
}

/**
 * Build a COMPLETE snapshot in one call — phase 1 and phase 2 accumulated
 * without publishing. `oracleSchemaCache` no longer uses this (it drives the
 * two phases itself so the relation list can publish first); it stays as the
 * one-shot form for callers that just want the finished object, and for the
 * unit tests that exercise the query set end to end.
 */
export async function buildOracleSchemaSnapshot(
  connectionId: string,
  schema: string,
): Promise<OracleSchemaSnapshot> {
  // The PK map has to exist before the column pages are aggregated (the flag
  // rides on the column), so it is awaited first rather than fanned out.
  const pksByTable = await fetchOraclePrimaryKeys(connectionId, schema).catch(
    () => new Map<string, Set<string>>(),
  );

  const [
    objectList,
    columnsByTable,
    sequences,
    synonyms,
    routines,
    packages,
    schemas,
    routinesByNameLower,
    foreignKeys,
    checkConstraintsByTable,
    serverVersion,
    userTypes,
    typeAttributesByTypeName,
    propertyGraphs,
  ] = await Promise.all([
    fetchTablesAndViews(connectionId, schema),
    fetchColumns(connectionId, schema, pksByTable),
    fetchSequences(connectionId, schema),
    fetchOracleSynonyms(connectionId, schema),
    fetchRoutines(connectionId, schema),
    fetchOraclePackages(connectionId, schema),
    fetchOracleSchemaNames(connectionId, schema),
    fetchOracleRoutineParams(connectionId, schema),
    fetchForeignKeys(connectionId, schema).catch(() => [] as OracleSchemaForeignKey[]),
    fetchCheckConstraints(connectionId, schema).catch(() => ({}) as Record<string, string[]>),
    fetchOracleServerVersion(connectionId, schema).catch(() => ''),
    fetchUserTypes(connectionId, schema).catch(() => [] as string[]),
    fetchUserTypeAttributes(connectionId, schema).catch(
      () => ({}) as Record<string, OracleTypeAttribute[]>,
    ),
    fetchOraclePropertyGraphs(connectionId, schema).catch(() => [] as OraclePropertyGraph[]),
  ]);

  const objects: OracleSchemaObject[] = objectList.map((obj) => {
    const checks = checkConstraintsByTable[obj.name];
    const base: OracleSchemaObject = {
      name: obj.name,
      schema: obj.owner,
      type: obj.type,
      columns: columnsByTable[obj.name] ?? [],
    };
    if (checks && checks.length > 0) base.checkConstraints = checks;
    return base;
  });

  const tables = objects.filter((obj) => obj.type === 'table');
  const views = objects.filter((obj) => obj.type === 'view' || obj.type === 'materialized_view');
  const lookups = buildLookupIndexes(objects);

  return {
    connectionId,
    schema,
    builtAt: Date.now(),
    tables,
    views,
    sequences,
    synonyms,
    routines,
    packages,
    schemas,
    ...lookups,
    routinesByNameLower,
    foreignKeys,
    serverVersion,
    userTypes,
    typeAttributesByTypeName,
    propertyGraphs,
    phase: 'complete',
  };
}
